// =============================================================================
// SHARE CONSEQUENCES MODAL - Viral card sharing for consequences
// =============================================================================

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  generateConsequencesCard,
  downloadConsequencesCard,
  shareConsequencesNative,
  copyConsequencesCard,
  type ConsequencesCardData,
} from '../../services/shareCardGenerator';
import {
  shareToTwitter,
  shareToTelegram,
  shareToFacebook,
  shareToThreads,
} from '../../services/shareService';
import { trackShare } from '../../services/analytics';

interface ShareConsequencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  consequences: string;
  decisionNumber: number;
}

export function ShareConsequencesModal({
  isOpen,
  onClose,
  consequences,
  decisionNumber,
}: ShareConsequencesModalProps) {
  const [cardDataUrl, setCardDataUrl] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shareStatus, setShareStatus] = useState<string | null>(null);

  const cardData: ConsequencesCardData = {
    consequences,
    decisionNumber,
  };

  const shareText = `⚡ НАСЛІДКИ РІШЕННЯ #${decisionNumber}

"${consequences}"

— Головнокомандувач
${window.location.origin}

#Головнокомандувач`;

  // Generate card when modal opens or data changes
  useEffect(() => {
    if (isOpen) {
      setIsGenerating(true);
      setCardDataUrl(null); // Clear old card first
      generateConsequencesCard({ consequences, decisionNumber })
        .then((dataUrl) => {
          setCardDataUrl(dataUrl);
          setIsGenerating(false);
        })
        .catch((err) => {
          console.error('Failed to generate card:', err);
          setIsGenerating(false);
        });
    }
  }, [isOpen, consequences, decisionNumber]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCopied(false);
      setShareStatus(null);
    }
  }, [isOpen]);

  const handleDownload = useCallback(async () => {
    trackShare('clipboard', 'stalemate');
    try {
      await downloadConsequencesCard(cardData);
      setShareStatus('Зображення завантажено');
      setTimeout(() => setShareStatus(null), 2000);
    } catch {
      setShareStatus('Помилка завантаження');
    }
  }, [cardData]);

  const handleNativeShare = useCallback(async () => {
    trackShare('native', 'stalemate');
    const success = await shareConsequencesNative(cardData);
    if (!success) {
      await handleDownload();
    }
  }, [cardData, handleDownload]);

  const handleCopyImage = useCallback(async () => {
    trackShare('clipboard', 'stalemate');
    const success = await copyConsequencesCard(cardData);
    if (success) {
      setCopied(true);
      setShareStatus('Зображення скопійовано');
      setTimeout(() => {
        setCopied(false);
        setShareStatus(null);
      }, 2000);
    } else {
      setShareStatus('Не вдалося скопіювати');
    }
  }, [cardData]);

  const handlePlatformShare = useCallback(
    async (
      platform: 'twitter' | 'telegram' | 'facebook' | 'threads' | 'instagram',
    ) => {
      trackShare(platform, 'stalemate');

      const url = window.location.origin;
      switch (platform) {
        case 'twitter':
          shareToTwitter(shareText);
          break;
        case 'telegram':
          shareToTelegram(shareText);
          break;
        case 'facebook':
          shareToFacebook(url, shareText);
          break;
        case 'threads':
          shareToThreads(shareText);
          break;
        case 'instagram':
          const imageCopied = await copyConsequencesCard(cardData);
          if (imageCopied) {
            setShareStatus('Зображення скопійовано — вставте в Instagram');
            setTimeout(() => setShareStatus(null), 3000);
          } else {
            await downloadConsequencesCard(cardData);
            setShareStatus('Завантажте і додайте в Instagram');
            setTimeout(() => setShareStatus(null), 3000);
          }
          break;
      }
    },
    [shareText, cardData],
  );

  const platforms = [
    { id: 'twitter' as const, name: 'X', icon: '𝕏' },
    { id: 'telegram' as const, name: 'Telegram', icon: '✈️' },
    { id: 'facebook' as const, name: 'Facebook', icon: 'f' },
    { id: 'instagram' as const, name: 'Instagram', icon: '📷' },
    { id: 'threads' as const, name: 'Threads', icon: '@' },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-war-bg/90 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-4 md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-2xl z-50 flex flex-col max-h-[90vh]"
          >
            <div className="glass-card p-4 md:p-6 relative flex flex-col overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-war-dim/20">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-war-red animate-pulse" />
                  <span className="text-war-primary text-xs font-mono uppercase tracking-widest">
                    Поділитися наслідками
                  </span>
                </div>
                <button
                  onClick={onClose}
                  className="text-war-dim hover:text-war-bright transition-colors text-lg p-1"
                  aria-label="Закрити"
                >
                  ✕
                </button>
              </div>

              {/* Card Preview */}
              <div className="flex-1 overflow-auto mb-4">
                <div className="relative aspect-[4/5] bg-war-bg/50 border border-war-dim/20 overflow-hidden">
                  {isGenerating ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="flex items-center gap-3 text-war-dim">
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{
                            repeat: Infinity,
                            duration: 1,
                            ease: 'linear',
                          }}
                          className="w-5 h-5 border-2 border-war-primary border-t-transparent rounded-full"
                        />
                        <span className="text-xs font-mono uppercase tracking-widest">
                          Генерація...
                        </span>
                      </div>
                    </div>
                  ) : cardDataUrl ? (
                    <img
                      src={cardDataUrl}
                      alt="Consequences Card Preview"
                      className="w-full h-full object-contain"
                    />
                  ) : null}
                </div>
              </div>

              {/* Status Message */}
              {shareStatus && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center text-war-primary text-xs font-mono uppercase tracking-widest mb-3"
                >
                  {shareStatus}
                </motion.div>
              )}

              {/* Action Buttons */}
              <div className="space-y-3">
                {/* Primary actions */}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={handleNativeShare}
                    className="py-3 px-4 bg-war-primary/10 border border-war-primary text-war-primary hover:bg-war-primary/20 transition-all font-mono text-xs uppercase tracking-widest flex items-center justify-center gap-2"
                  >
                    📤 Поділитися
                  </button>
                  <button
                    onClick={handleDownload}
                    className="py-3 px-4 bg-war-panel border border-war-dim/30 text-war-dim hover:text-war-bright hover:border-war-dim transition-all font-mono text-xs uppercase tracking-widest flex items-center justify-center gap-2"
                  >
                    ⬇️ Завантажити
                  </button>
                </div>

                {/* Copy image button */}
                <button
                  onClick={handleCopyImage}
                  className={`w-full py-2 px-4 border transition-all font-mono text-xs uppercase tracking-widest ${
                    copied
                      ? 'bg-war-primary/20 border-war-primary text-war-primary'
                      : 'bg-war-panel border-war-dim/30 text-war-dim hover:text-war-bright hover:border-war-dim'
                  }`}
                >
                  {copied ? '✓ Скопійовано' : '📋 Копіювати зображення'}
                </button>

                {/* Platform buttons */}
                <div className="grid grid-cols-5 gap-2">
                  {platforms.map((platform) => (
                    <button
                      key={platform.id}
                      onClick={() => handlePlatformShare(platform.id)}
                      className="group flex flex-col items-center justify-center gap-1 py-2 px-2 bg-war-panel border border-war-dim/30 hover:border-war-dim transition-all"
                    >
                      <span className="text-lg">{platform.icon}</span>
                      <span className="text-[9px] font-mono text-war-dim group-hover:text-war-bright uppercase">
                        {platform.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Corner accents */}
              <div className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-war-primary" />
              <div className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-war-primary" />
              <div className="absolute bottom-0 left-0 w-3 h-3 border-b-2 border-l-2 border-war-primary" />
              <div className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-war-primary" />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
